<template>
  <div class="tech-page">
    <div class="tech-decor">
      <div class="decor-orb orb-1"></div>
      <div class="decor-orb orb-2"></div>
      <div class="decor-orb orb-3"></div>
      <div class="decor-line line-1"></div>
      <div class="decor-line line-2"></div>
      <div class="decor-line line-3"></div>
    </div>

    <!-- ======================== HERO ======================== -->
    <section class="tech-hero">
      <div class="tech-hero-inner">
        <div class="tech-badge">{{ localize('TECH_BADGE') }}</div>
        <h1 class="tech-title">{{ localize('TECH_TITLE') }}</h1>
        <p class="tech-subtitle">{{ localize('TECH_SUBTITLE') }}</p>
      </div>
    </section>

    <!-- ======================== CORE ARCHITECTURE ======================== -->
    <section class="tech-section">
      <div class="section-container">
        <div class="section-badge">{{ localize('TECH_ARCH_BADGE') }}</div>
        <h2 class="section-heading">{{ localize('TECH_ARCH_HEADING') }}</h2>

        <div class="arch-grid">
          <div class="arch-card">
            <div class="arch-card-icon" v-html="getRawIcon('ICON_SHIELD')"></div>
            <h3 class="arch-card-title">{{ localize('TECH_ARCH_DETECT_TITLE') }}</h3>
            <div class="arch-card-mono">{{ localize('TECH_ARCH_DETECT_MONO') }}</div>
            <p class="arch-card-text">{{ localize('TECH_ARCH_DETECT_DESC') }}</p>
            <ul class="arch-card-list">
              <li>{{ localize('TECH_ARCH_DETECT_1') }}</li>
              <li>{{ localize('TECH_ARCH_DETECT_2') }}</li>
              <li>{{ localize('TECH_ARCH_DETECT_3') }}</li>
            </ul>
          </div>

          <div class="arch-card">
            <div class="arch-card-icon" v-html="getRawIcon('ICON_BRAIN')"></div>
            <h3 class="arch-card-title">{{ localize('TECH_ARCH_AI_TITLE') }}</h3>
            <div class="arch-card-mono">{{ localize('TECH_ARCH_AI_MONO') }}</div>
            <p class="arch-card-text">{{ localize('TECH_ARCH_AI_DESC') }}</p>
            <ul class="arch-card-list">
              <li>{{ localize('TECH_ARCH_AI_1') }}</li>
              <li>{{ localize('TECH_ARCH_AI_2') }}</li>
              <li>{{ localize('TECH_ARCH_AI_3') }}</li>
            </ul>
          </div>

          <div class="arch-card">
            <div class="arch-card-icon" v-html="getRawIcon('ICON_NETWORK')"></div>
            <h3 class="arch-card-title">{{ localize('TECH_ARCH_MESH_TITLE') }}</h3>
            <div class="arch-card-mono">{{ localize('TECH_ARCH_MESH_MONO') }}</div>
            <p class="arch-card-text">{{ localize('TECH_ARCH_MESH_DESC') }}</p>
            <ul class="arch-card-list">
              <li>{{ localize('TECH_ARCH_MESH_1') }}</li>
              <li>{{ localize('TECH_ARCH_MESH_2') }}</li>
              <li>{{ localize('TECH_ARCH_MESH_3') }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- ======================== DETECTION FLOW ======================== -->
    <section class="tech-section">
      <div class="section-container">
        <div class="section-badge">{{ localize('TECH_FLOW_BADGE') }}</div>
        <h2 class="section-heading">{{ localize('TECH_FLOW_HEADING') }}</h2>

        <div class="flow-timeline">
          <div class="flow-step" v-for="step in detectionSteps" :key="step.num">
            <div class="flow-step-number">{{ step.num }}</div>
            <div class="flow-step-connector"></div>
            <div class="flow-step-card">
              <div class="flow-step-header">
                <div class="flow-step-icon" v-html="getRawIcon(step.icon)"></div>
                <div>
                  <h4 class="flow-step-title">{{ localize(step.titleKey) }}</h4>
                  <span class="flow-step-timing">{{ localize(step.timingKey) }}</span>
                </div>
              </div>
              <p class="flow-step-desc">{{ localize(step.descKey) }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ======================== RESPONSE FLOW ======================== -->
    <section class="tech-section">
      <div class="section-container">
        <div class="section-badge">{{ localize('TECH_RESPONSE_BADGE') }}</div>
        <h2 class="section-heading">{{ localize('TECH_RESPONSE_HEADING') }}</h2>

        <div class="flow-timeline">
          <div class="flow-step" v-for="step in responseSteps" :key="step.num">
            <div class="flow-step-number">{{ step.num }}</div>
            <div class="flow-step-connector"></div>
            <div class="flow-step-card">
              <div class="flow-step-header">
                <div class="flow-step-icon" v-html="getRawIcon(step.icon)"></div>
                <div>
                  <h4 class="flow-step-title">{{ localize(step.titleKey) }}</h4>
                  <span class="flow-step-timing">{{ localize(step.timingKey) }}</span>
                </div>
              </div>
              <p class="flow-step-desc">{{ localize(step.descKey) }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ======================== MITRE ATT&CK ======================== -->
    <section class="tech-section">
      <div class="section-container">
        <div class="section-badge">{{ localize('TECH_MITRE_BADGE') }}</div>
        <h2 class="section-heading">{{ localize('TECH_MITRE_HEADING') }}</h2>

        <div class="mitre-grid">
          <div class="mitre-card" v-for="stat in mitreStats" :key="stat.valueKey">
            <div class="mitre-icon" v-html="getRawIcon(stat.icon)"></div>
            <div class="mitre-value">{{ localize(stat.valueKey) }}</div>
            <div class="mitre-label">{{ localize(stat.labelKey) }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ======================== ZERO KNOWLEDGE ======================== -->
    <section class="tech-section">
      <div class="section-container">
        <div class="section-badge">{{ localize('TECH_ZK_BADGE') }}</div>
        <h2 class="section-heading">{{ localize('TECH_ZK_HEADING') }}</h2>

        <div class="zk-card">
          <div class="zk-card-icon" v-html="getRawIcon('ICON_EYE_OFF')"></div>
          <div class="zk-card-body">
            <h3 class="zk-card-title">{{ localize('TECH_ZK_TITLE') }}</h3>
            <p class="zk-card-text">{{ localize('TECH_ZK_DESC_1') }}</p>
            <p class="zk-card-text">{{ localize('TECH_ZK_DESC_2') }}</p>
            <div class="zk-features">
              <div class="zk-feature">
                <div class="zk-feature-dot"></div>
                <span>{{ localize('TECH_ZK_FEAT_1') }}</span>
              </div>
              <div class="zk-feature">
                <div class="zk-feature-dot"></div>
                <span>{{ localize('TECH_ZK_FEAT_2') }}</span>
              </div>
              <div class="zk-feature">
                <div class="zk-feature-dot"></div>
                <span>{{ localize('TECH_ZK_FEAT_3') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ======================== DEPLOYMENT ======================== -->
    <section class="tech-section">
      <div class="section-container">
        <div class="section-badge">{{ localize('TECH_DEPLOY_BADGE') }}</div>
        <h2 class="section-heading">{{ localize('TECH_DEPLOY_HEADING') }}</h2>

        <div class="deploy-grid">
          <div class="deploy-card" v-for="dep in deployOptions" :key="dep.titleKey">
            <div class="deploy-card-icon" v-html="getRawIcon(dep.icon)"></div>
            <h4 class="deploy-card-title">{{ localize(dep.titleKey) }}</h4>
            <p class="deploy-card-desc">{{ localize(dep.descKey) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ======================== OPEN SOURCE ======================== -->
    <section class="tech-section">
      <div class="section-container">
        <div class="section-badge">{{ localize('TECH_OSS_BADGE') }}</div>
        <h2 class="section-heading">{{ localize('TECH_OSS_HEADING') }}</h2>

        <div class="oss-card">
          <p class="oss-text">{{ localize('TECH_OSS_DESC_1') }}</p>
          <p class="oss-text">{{ localize('TECH_OSS_DESC_2') }}</p>

          <div class="oss-extend-label">{{ localize('TECH_OSS_EXTEND_LABEL') }}</div>
          <div class="oss-extend-grid">
            <div class="oss-extend-item" v-for="ext in ossExtensions" :key="ext">
              <div class="oss-extend-bullet"></div>
              <span>{{ localize(ext) }}</span>
            </div>
          </div>

          <div class="oss-tech-strip">
            <span class="oss-tech-tag" v-for="tag in ossTechTags" :key="tag">{{ localize(tag) }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ======================== PERFORMANCE METRICS ======================== -->
    <section class="tech-section section-last">
      <div class="section-container">
        <div class="section-badge">{{ localize('TECH_PERF_BADGE') }}</div>
        <h2 class="section-heading">{{ localize('TECH_PERF_HEADING') }}</h2>

        <div class="perf-grid">
          <div class="perf-card" v-for="metric in perfMetrics" :key="metric.valueKey">
            <div class="perf-card-shine"></div>
            <div class="perf-icon" v-html="getRawIcon(metric.icon)"></div>
            <div class="perf-value">{{ localize(metric.valueKey) }}</div>
            <div class="perf-label">{{ localize(metric.labelKey) }}</div>
            <div class="perf-desc">{{ localize(metric.descKey) }}</div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>

import {translationStore} from "@/stores/translation";
import {iconStore} from "@/stores/icon";

export default {
    "name": "Technology",
    setup () {

        const translation = translationStore();
        const icon = iconStore();

        const getRawIcon = function (i) {
            return icon.get(i) || '';
        };

        const detectionSteps = [
            { num: '01', icon: 'ICON_CPU',       titleKey: 'TECH_FLOW_S1_TITLE', timingKey: 'TECH_FLOW_S1_TIMING', descKey: 'TECH_FLOW_S1_DESC' },
            { num: '02', icon: 'ICON_ACTIVITY',  titleKey: 'TECH_FLOW_S2_TITLE', timingKey: 'TECH_FLOW_S2_TIMING', descKey: 'TECH_FLOW_S2_DESC' },
            { num: '03', icon: 'ICON_LAYERS',    titleKey: 'TECH_FLOW_S3_TITLE', timingKey: 'TECH_FLOW_S3_TIMING', descKey: 'TECH_FLOW_S3_DESC' },
            { num: '04', icon: 'ICON_CROSSHAIR', titleKey: 'TECH_FLOW_S4_TITLE', timingKey: 'TECH_FLOW_S4_TIMING', descKey: 'TECH_FLOW_S4_DESC' },
            { num: '05', icon: 'ICON_RADAR',     titleKey: 'TECH_FLOW_S5_TITLE', timingKey: 'TECH_FLOW_S5_TIMING', descKey: 'TECH_FLOW_S5_DESC' }
        ];

        const responseSteps = [
            { num: '01', icon: 'ICON_BOLT',      titleKey: 'TECH_RESP_S1_TITLE', timingKey: 'TECH_RESP_S1_TIMING', descKey: 'TECH_RESP_S1_DESC' },
            { num: '02', icon: 'ICON_MAP',       titleKey: 'TECH_RESP_S2_TITLE', timingKey: 'TECH_RESP_S2_TIMING', descKey: 'TECH_RESP_S2_DESC' },
            { num: '03', icon: 'ICON_TERMINAL',  titleKey: 'TECH_RESP_S3_TITLE', timingKey: 'TECH_RESP_S3_TIMING', descKey: 'TECH_RESP_S3_DESC' },
            { num: '04', icon: 'ICON_BRAIN',     titleKey: 'TECH_RESP_S4_TITLE', timingKey: 'TECH_RESP_S4_TIMING', descKey: 'TECH_RESP_S4_DESC' },
            { num: '05', icon: 'ICON_LOCK',      titleKey: 'TECH_RESP_S5_TITLE', timingKey: 'TECH_RESP_S5_TIMING', descKey: 'TECH_RESP_S5_DESC' }
        ];

        const mitreStats = [
            { icon: 'ICON_CROSSHAIR', valueKey: 'TECH_MITRE_V1', labelKey: 'TECH_MITRE_L1' },
            { icon: 'ICON_LAYERS',    valueKey: 'TECH_MITRE_V2', labelKey: 'TECH_MITRE_L2' },
            { icon: 'ICON_SHIELD',    valueKey: 'TECH_MITRE_V3', labelKey: 'TECH_MITRE_L3' },
            { icon: 'ICON_BRAIN',     valueKey: 'TECH_MITRE_V4', labelKey: 'TECH_MITRE_L4' }
        ];

        const deployOptions = [
            { icon: 'ICON_GLOBE',    titleKey: 'TECH_DEPLOY_CLOUD_TITLE',   descKey: 'TECH_DEPLOY_CLOUD_DESC' },
            { icon: 'ICON_DATABASE', titleKey: 'TECH_DEPLOY_ONPREM_TITLE',  descKey: 'TECH_DEPLOY_ONPREM_DESC' },
            { icon: 'ICON_LINK',     titleKey: 'TECH_DEPLOY_HYBRID_TITLE',  descKey: 'TECH_DEPLOY_HYBRID_DESC' },
            { icon: 'ICON_BOX',      titleKey: 'TECH_DEPLOY_BYOAI_TITLE',   descKey: 'TECH_DEPLOY_BYOAI_DESC' }
        ];

        const ossExtensions = [
            'TECH_OSS_EXT_1',
            'TECH_OSS_EXT_2',
            'TECH_OSS_EXT_3',
            'TECH_OSS_EXT_4'
        ];

        const ossTechTags = [
            'TECH_OSS_TAG_1',
            'TECH_OSS_TAG_2',
            'TECH_OSS_TAG_3',
            'TECH_OSS_TAG_4',
            'TECH_OSS_TAG_5',
            'TECH_OSS_TAG_6'
        ];

        const perfMetrics = [
            { icon: 'ICON_BOLT',     valueKey: 'TECH_PERF_V1', labelKey: 'TECH_PERF_L1', descKey: 'TECH_PERF_D1' },
            { icon: 'ICON_BRAIN',    valueKey: 'TECH_PERF_V2', labelKey: 'TECH_PERF_L2', descKey: 'TECH_PERF_D2' },
            { icon: 'ICON_SHIELD',   valueKey: 'TECH_PERF_V3', labelKey: 'TECH_PERF_L3', descKey: 'TECH_PERF_D3' }
        ];

        return {
            "localize": translation.localize,
            getRawIcon,
            detectionSteps,
            responseSteps,
            mitreStats,
            deployOptions,
            ossExtensions,
            ossTechTags,
            perfMetrics
        };

    }
};

</script>

<style scoped>
/* ======== BASE ======== */
.tech-page {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background: var(--bg-primary);
  font-family: var(--font-body);
  color: var(--text-primary);
}

/* ======== Decorative Background ======== */
.tech-decor {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}
.decor-orb {
  position: absolute;
  border-radius: 50%;
  opacity: 0.15;
}
.orb-1 {
  width: 700px;
  height: 700px;
  background: radial-gradient(circle, rgba(124, 58, 237, 0.22), rgba(6, 182, 212, 0.1) 50%, transparent 70%);
  top: -200px;
  right: -150px;
  animation: drift-a 28s ease-in-out infinite;
}
.orb-2 {
  width: 550px;
  height: 550px;
  background: radial-gradient(circle, rgba(6, 182, 212, 0.18), rgba(236, 72, 153, 0.08) 50%, transparent 70%);
  bottom: 15%;
  left: -120px;
  animation: drift-b 32s ease-in-out infinite;
}
.orb-3 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(236, 72, 153, 0.15), transparent 60%);
  top: 50%;
  right: 10%;
  animation: drift-c 24s ease-in-out infinite;
}
.decor-line {
  position: absolute;
  background: linear-gradient(180deg, transparent, rgba(124, 58, 237, 0.12), transparent);
  opacity: 0.6;
  width: 1px;
}
.line-1 { height: 55%; top: 8%; left: 12%; }
.line-2 { height: 45%; top: 25%; right: 18%; }
.line-3 { height: 35%; top: 40%; left: 50%; }

@keyframes drift-a {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-50px, 70px); }
}
@keyframes drift-b {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(60px, -50px); }
}
@keyframes drift-c {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-30px, -40px); }
}

/* ======== HERO ======== */
.tech-hero {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  padding: 120px 24px 80px;
  text-align: center;
  background: transparent;
}
.tech-hero-inner {
  max-width: 780px;
}
.tech-badge {
  display: inline-block;
  padding: 10px 26px;
  margin-bottom: 34px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: #fff;
  border: none;
  border-radius: 20px;
  background: var(--gradient-primary);
  box-shadow: 0 4px 15px rgba(124, 58, 237, 0.3);
}
.tech-title {
  font-family: var(--font-heading);
  font-size: 3.4em;
  font-weight: 800;
  line-height: 1.1;
  margin: 0 0 28px;
  letter-spacing: -1.5px;
  background: var(--gradient-text);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.tech-subtitle {
  font-family: var(--font-body);
  font-size: 1.15em;
  line-height: 1.8;
  color: var(--text-secondary);
  margin: 0;
  max-width: 620px;
  margin-left: auto;
  margin-right: auto;
}
.tech-hero::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: min(85%, 700px);
  height: 3px;
  background: var(--gradient-divider);
}

/* ======== SECTIONS ======== */
.tech-section {
  position: relative;
  z-index: 1;
  padding: 80px 24px;
}
.tech-section:nth-child(odd) {
  background: rgba(124, 58, 237, 0.03);
}
.section-last {
  padding-bottom: 120px;
}
.section-container {
  max-width: 1100px;
  margin: 0 auto;
}
.section-badge {
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: #fff;
  background: var(--gradient-primary);
  border-radius: 20px;
  border: none;
  padding: 8px 22px;
  margin-bottom: 18px;
  box-shadow: 0 4px 15px rgba(124, 58, 237, 0.3);
}
.section-heading {
  font-family: var(--font-heading);
  font-size: 2.4em;
  font-weight: 800;
  margin: 0 0 52px;
  letter-spacing: -1px;
  line-height: 1.2;
  background: var(--gradient-text);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ======== CORE ARCHITECTURE CARDS ======== */
.arch-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 36px;
}
.arch-card {
  position: relative;
  overflow: hidden;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 2px;
  padding: 40px 32px 36px;
  border-left: 4px solid transparent;
  box-shadow: 6px 6px 0 rgba(124,58,237,0.28), 0 10px 40px rgba(124,58,237,0.16);
  transition: box-shadow 0.35s ease, transform 0.35s ease;
}
.arch-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, var(--purple), var(--cyan));
}
.arch-card:nth-child(2)::before {
  background: linear-gradient(180deg, var(--cyan), var(--magenta));
}
.arch-card:nth-child(3)::before {
  background: linear-gradient(180deg, var(--magenta), var(--purple));
}
.arch-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #7c3aed, #06b6d4, #ec4899);
  opacity: 0.5;
}
.arch-card:hover {
  box-shadow: 8px 8px 0 rgba(124,58,237,0.35), 0 16px 48px rgba(124,58,237,0.22);
  transform: translateY(-3px);
}
.arch-card-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 24px;
  color: var(--purple);
  background: rgba(124, 58, 237, 0.08);
  border: 1px solid rgba(124, 58, 237, 0.2);
  border-radius: 2px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.arch-card-icon :deep(svg) {
  width: 32px;
  height: 32px;
}
.arch-card-title {
  font-family: var(--font-heading);
  font-size: 1.35em;
  font-weight: 700;
  margin: 0 0 10px;
  letter-spacing: -0.3px;
  color: var(--text-primary);
}
.arch-card-mono {
  font-family: var(--font-mono);
  font-size: 11.5px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 18px;
  color: var(--cyan);
  background: rgba(6, 182, 212, 0.08);
  display: inline-block;
  padding: 5px 14px;
  border-radius: 2px;
  border: 1px solid rgba(6, 182, 212, 0.25);
}
.arch-card-text {
  font-size: 0.93em;
  color: var(--text-secondary);
  line-height: 1.8;
  margin: 0 0 20px;
}
.arch-card-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.arch-card-list li {
  position: relative;
  padding-left: 20px;
  font-size: 0.9em;
  color: var(--text-secondary);
  line-height: 1.75;
  margin-bottom: 8px;
}
.arch-card-list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  width: 8px;
  height: 8px;
  background: var(--purple);
  border-radius: 2px;
}

/* ======== FLOW TIMELINE ======== */
.flow-timeline {
  position: relative;
  padding-left: 56px;
}
.flow-timeline::before {
  content: '';
  position: absolute;
  left: 18px;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, var(--purple), var(--cyan), var(--magenta));
}
.flow-step {
  position: relative;
  margin-bottom: 32px;
}
.flow-step:last-child {
  margin-bottom: 0;
}
.flow-step:last-child .flow-step-connector {
  display: none;
}
.flow-step-number {
  position: absolute;
  left: -56px;
  top: 0;
  width: 40px;
  height: 40px;
  border-radius: 2px;
  background: linear-gradient(135deg, var(--purple), var(--purple-deep));
  color: #fff;
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  box-shadow: 3px 3px 0 rgba(124,58,237,0.3);
}
.flow-step-connector {
  position: absolute;
  left: -37px;
  top: 40px;
  width: 3px;
  bottom: -32px;
  background: transparent;
}
.flow-step-card {
  position: relative;
  overflow: hidden;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 2px;
  padding: 28px 30px;
  border-left: 4px solid transparent;
  box-shadow: 6px 6px 0 rgba(124,58,237,0.28), 0 10px 40px rgba(124,58,237,0.16);
  transition: box-shadow 0.35s ease, transform 0.35s ease;
}
.flow-step-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, var(--purple), var(--cyan));
}
.flow-step:nth-child(1) .flow-step-card::before {
  background: linear-gradient(180deg, var(--purple), var(--cyan));
}
.flow-step:nth-child(2) .flow-step-card::before {
  background: linear-gradient(180deg, var(--magenta), var(--purple));
}
.flow-step:nth-child(3) .flow-step-card::before {
  background: linear-gradient(180deg, var(--cyan), var(--magenta));
}
.flow-step:nth-child(4) .flow-step-card::before {
  background: linear-gradient(180deg, var(--purple-deep), var(--cyan));
}
.flow-step:nth-child(5) .flow-step-card::before {
  background: linear-gradient(180deg, var(--magenta), var(--purple));
}
.flow-step-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #7c3aed, #06b6d4, #ec4899);
  opacity: 0.5;
}
.flow-step-card:hover {
  box-shadow: 8px 8px 0 rgba(124,58,237,0.35), 0 16px 48px rgba(124,58,237,0.22);
  transform: translateY(-3px);
}
.flow-step-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 14px;
}
.flow-step-icon {
  width: 32px;
  height: 32px;
  color: var(--purple);
  flex-shrink: 0;
}
.flow-step-icon :deep(svg) {
  width: 32px;
  height: 32px;
}
.flow-step-title {
  font-family: var(--font-heading);
  font-size: 1.12em;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.2px;
  color: var(--text-primary);
}
.flow-step-timing {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--cyan);
  letter-spacing: 1.5px;
  text-transform: uppercase;
  font-weight: 700;
}
.flow-step-desc {
  font-size: 0.92em;
  color: var(--text-secondary);
  line-height: 1.75;
  margin: 0;
}

/* ======== MITRE ATT&CK STATS ======== */
.mitre-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 36px;
}
.mitre-card {
  position: relative;
  background: var(--bg-card);
  padding: 40px 24px;
  text-align: center;
  border-radius: 2px;
  border: 1px solid var(--border-subtle);
  border-left: 4px solid transparent;
  box-shadow: 6px 6px 0 rgba(124,58,237,0.28), 0 10px 40px rgba(124,58,237,0.16);
  transition: box-shadow 0.35s ease, transform 0.35s ease;
  overflow: hidden;
}
.mitre-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, var(--purple), var(--cyan));
}
.mitre-card:nth-child(2)::before {
  background: linear-gradient(180deg, var(--cyan), var(--magenta));
}
.mitre-card:nth-child(3)::before {
  background: linear-gradient(180deg, var(--magenta), var(--purple));
}
.mitre-card:nth-child(4)::before {
  background: linear-gradient(180deg, var(--purple-deep), var(--cyan));
}
.mitre-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #7c3aed, #06b6d4, #ec4899);
  opacity: 0.5;
}
.mitre-card:hover {
  box-shadow: 8px 8px 0 rgba(124,58,237,0.35), 0 16px 48px rgba(124,58,237,0.22);
  transform: translateY(-3px);
}
.mitre-icon {
  width: 36px;
  height: 36px;
  margin: 0 auto 20px;
  color: var(--purple);
}
.mitre-icon :deep(svg) {
  width: 36px;
  height: 36px;
}
.mitre-value {
  font-family: var(--font-heading);
  font-size: 4.2em;
  font-weight: 800;
  letter-spacing: -2px;
  margin-bottom: 10px;
  line-height: 1;
  background: var(--gradient-text);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.mitre-label {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-muted);
  letter-spacing: 2px;
  text-transform: uppercase;
  line-height: 1.5;
  font-weight: 600;
}

/* ======== ZERO KNOWLEDGE ======== */
.zk-card {
  position: relative;
  overflow: hidden;
  display: flex;
  gap: 40px;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 2px;
  padding: 48px 44px;
  border-left: 4px solid transparent;
  box-shadow: 6px 6px 0 rgba(124,58,237,0.28), 0 10px 40px rgba(124,58,237,0.16);
  transition: box-shadow 0.35s ease, transform 0.35s ease;
}
.zk-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, var(--purple), var(--cyan));
}
.zk-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #7c3aed, #06b6d4, #ec4899);
  opacity: 0.5;
}
.zk-card:hover {
  box-shadow: 8px 8px 0 rgba(124,58,237,0.35), 0 16px 48px rgba(124,58,237,0.22);
  transform: translateY(-3px);
}
.zk-card-icon {
  width: 64px;
  height: 64px;
  color: var(--purple);
  flex-shrink: 0;
  background: rgba(124, 58, 237, 0.08);
  border: 1px solid rgba(124, 58, 237, 0.2);
  border-radius: 2px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.zk-card-icon :deep(svg) {
  width: 40px;
  height: 40px;
}
.zk-card-body {
  flex: 1;
}
.zk-card-title {
  font-family: var(--font-heading);
  font-size: 1.5em;
  font-weight: 800;
  margin: 0 0 20px;
  letter-spacing: -0.3px;
  color: var(--text-primary);
}
.zk-card-text {
  font-size: 0.95em;
  color: var(--text-secondary);
  line-height: 1.8;
  margin: 0 0 14px;
}
.zk-features {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 24px;
}
.zk-feature {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 0.92em;
  color: var(--text-secondary);
  font-weight: 500;
}
.zk-feature-dot {
  width: 22px;
  height: 22px;
  background: linear-gradient(135deg, var(--purple), var(--purple-deep));
  flex-shrink: 0;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.zk-feature-dot::after {
  content: '';
  width: 10px;
  height: 6px;
  border-left: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: rotate(-45deg) translateY(-1px);
}

/* ======== DEPLOYMENT GRID ======== */
.deploy-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 36px;
}
.deploy-card {
  position: relative;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 2px;
  padding: 36px 28px;
  border-left: 4px solid transparent;
  box-shadow: 6px 6px 0 rgba(124,58,237,0.28), 0 10px 40px rgba(124,58,237,0.16);
  transition: box-shadow 0.35s ease, transform 0.35s ease;
  overflow: hidden;
}
.deploy-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, var(--purple), var(--cyan));
}
.deploy-card:nth-child(2)::before {
  background: linear-gradient(180deg, var(--magenta), var(--purple));
}
.deploy-card:nth-child(3)::before {
  background: linear-gradient(180deg, var(--cyan), var(--magenta));
}
.deploy-card:nth-child(4)::before {
  background: linear-gradient(180deg, var(--purple-deep), var(--cyan));
}
.deploy-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #7c3aed, #06b6d4, #ec4899);
  opacity: 0.5;
}
.deploy-card:hover {
  box-shadow: 8px 8px 0 rgba(124,58,237,0.35), 0 16px 48px rgba(124,58,237,0.22);
  transform: translateY(-3px);
}
.deploy-card-icon {
  width: 36px;
  height: 36px;
  margin-bottom: 20px;
  color: var(--purple);
  background: rgba(124, 58, 237, 0.08);
  border: 1px solid rgba(124, 58, 237, 0.2);
  border-radius: 2px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.deploy-card-icon :deep(svg) {
  width: 24px;
  height: 24px;
}
.deploy-card-title {
  font-family: var(--font-heading);
  font-size: 1.1em;
  font-weight: 700;
  margin: 0 0 12px;
  color: var(--text-primary);
}
.deploy-card-desc {
  font-size: 0.9em;
  color: var(--text-secondary);
  line-height: 1.75;
  margin: 0;
}

/* ======== OPEN SOURCE ======== */
.oss-card {
  position: relative;
  overflow: hidden;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 2px;
  padding: 48px 44px;
  border-left: 4px solid transparent;
  box-shadow: 6px 6px 0 rgba(124,58,237,0.28), 0 10px 40px rgba(124,58,237,0.16);
  transition: box-shadow 0.35s ease, transform 0.35s ease;
}
.oss-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, var(--purple), var(--cyan));
}
.oss-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #7c3aed, #06b6d4, #ec4899);
  opacity: 0.5;
}
.oss-card:hover {
  box-shadow: 8px 8px 0 rgba(124,58,237,0.35), 0 16px 48px rgba(124,58,237,0.22);
  transform: translateY(-3px);
}
.oss-text {
  font-size: 0.96em;
  color: var(--text-secondary);
  line-height: 1.85;
  margin: 0 0 16px;
  max-width: 820px;
}
.oss-extend-label {
  font-family: var(--font-mono);
  font-size: 11.5px;
  color: var(--cyan);
  background: rgba(6, 182, 212, 0.08);
  display: inline-block;
  padding: 6px 16px;
  border-radius: 2px;
  border: 1px solid rgba(6, 182, 212, 0.25);
  letter-spacing: 2.5px;
  text-transform: uppercase;
  margin: 32px 0 20px;
  font-weight: 700;
}
.oss-extend-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px 40px;
  margin-bottom: 36px;
}
.oss-extend-item {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 0.92em;
  color: var(--text-secondary);
}
.oss-extend-bullet {
  width: 8px;
  height: 8px;
  background: var(--purple);
  flex-shrink: 0;
  border-radius: 2px;
}
.oss-tech-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding-top: 28px;
  border-top: 2px solid var(--border-subtle);
}
.oss-tech-tag {
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 1px;
  font-weight: 600;
  color: var(--cyan);
  background: rgba(6, 182, 212, 0.08);
  border: 1px solid rgba(6, 182, 212, 0.25);
  padding: 8px 16px;
  border-radius: 2px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.oss-tech-tag:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(6, 182, 212, 0.2);
}

/* ======== PERFORMANCE METRICS ======== */
.perf-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 36px;
}
.perf-card {
  position: relative;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: 2px;
  padding: 48px 32px;
  text-align: center;
  overflow: hidden;
  border-left: 4px solid transparent;
  box-shadow: 6px 6px 0 rgba(124,58,237,0.28), 0 10px 40px rgba(124,58,237,0.16);
  transition: transform 0.35s ease, box-shadow 0.35s ease;
}
.perf-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, var(--purple), var(--cyan));
}
.perf-card:nth-child(2)::before {
  background: linear-gradient(180deg, var(--cyan), var(--magenta));
}
.perf-card:nth-child(3)::before {
  background: linear-gradient(180deg, var(--magenta), var(--purple));
}
.perf-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #7c3aed, #06b6d4, #ec4899);
  opacity: 0.5;
}
.perf-card:hover {
  box-shadow: 8px 8px 0 rgba(124,58,237,0.35), 0 16px 48px rgba(124,58,237,0.22);
  transform: translateY(-3px);
}
.perf-card-shine {
  display: none;
}
.perf-icon {
  width: 40px;
  height: 40px;
  margin: 0 auto 24px;
  color: var(--purple);
  background: rgba(124, 58, 237, 0.08);
  border: 1px solid rgba(124, 58, 237, 0.2);
  border-radius: 2px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.perf-icon :deep(svg) {
  width: 28px;
  height: 28px;
}
.perf-value {
  font-family: var(--font-heading);
  font-size: 4em;
  font-weight: 800;
  letter-spacing: -2px;
  margin-bottom: 10px;
  line-height: 1;
  background: var(--gradient-text);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.perf-label {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-muted);
  letter-spacing: 2.5px;
  text-transform: uppercase;
  margin-bottom: 16px;
  font-weight: 600;
}
.perf-desc {
  font-size: 0.9em;
  color: var(--text-secondary);
  line-height: 1.65;
}

/* ======== DARK MODE ======== */
:root[data-theme="dark"] .tech-hero {
  background: transparent;
}
:root[data-theme="dark"] .tech-badge {
  color: #fff;
  border: none;
  background: var(--gradient-primary);
  box-shadow: 0 4px 15px rgba(124, 58, 237, 0.4);
}
:root[data-theme="dark"] .tech-subtitle {
  color: rgba(200, 200, 220, 0.75);
}
:root[data-theme="dark"] .tech-title {
  background: var(--gradient-text);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
:root[data-theme="dark"] .tech-hero::after {
  background: var(--gradient-divider);
}
:root[data-theme="dark"] .tech-section:nth-child(odd) {
  background: rgba(124, 58, 237, 0.03);
}
:root[data-theme="dark"] .section-badge {
  color: #fff;
  background: var(--gradient-primary);
  box-shadow: 0 4px 15px rgba(124, 58, 237, 0.4);
}
:root[data-theme="dark"] .section-heading {
  background: var(--gradient-text);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
:root[data-theme="dark"] .arch-card {
  background: var(--bg-card);
  border-color: rgba(124, 58, 237, 0.15);
  box-shadow: 6px 6px 0 rgba(124,58,237,0.3), 0 10px 40px rgba(0,0,0,0.25);
}
:root[data-theme="dark"] .arch-card:hover {
  box-shadow: 8px 8px 0 rgba(124,58,237,0.4), 0 16px 48px rgba(0,0,0,0.35);
}
:root[data-theme="dark"] .arch-card-title {
  color: var(--text-primary);
}
:root[data-theme="dark"] .arch-card-mono {
  color: #67e8f9;
  background: rgba(6, 182, 212, 0.1);
  border-color: rgba(6, 182, 212, 0.3);
}
:root[data-theme="dark"] .arch-card-icon {
  color: #a78bfa;
  background: rgba(124, 58, 237, 0.1);
  border-color: rgba(124, 58, 237, 0.25);
}
:root[data-theme="dark"] .flow-timeline::before {
  background: linear-gradient(180deg, #a78bfa, #67e8f9, #f9a8d4);
}
:root[data-theme="dark"] .flow-step-number {
  background: linear-gradient(135deg, #a78bfa, var(--purple-deep));
  box-shadow: 3px 3px 0 rgba(124,58,237,0.35);
}
:root[data-theme="dark"] .flow-step-card {
  background: var(--bg-card);
  border-color: rgba(124, 58, 237, 0.15);
  box-shadow: 6px 6px 0 rgba(124,58,237,0.3), 0 10px 40px rgba(0,0,0,0.25);
}
:root[data-theme="dark"] .flow-step-card:hover {
  box-shadow: 8px 8px 0 rgba(124,58,237,0.4), 0 16px 48px rgba(0,0,0,0.35);
}
:root[data-theme="dark"] .flow-step-title {
  color: var(--text-primary);
}
:root[data-theme="dark"] .flow-step-timing {
  color: #67e8f9;
}
:root[data-theme="dark"] .flow-step-icon {
  color: #a78bfa;
}
:root[data-theme="dark"] .mitre-card {
  background: var(--bg-card);
  border-color: rgba(124, 58, 237, 0.15);
  box-shadow: 6px 6px 0 rgba(124,58,237,0.3), 0 10px 40px rgba(0,0,0,0.25);
}
:root[data-theme="dark"] .mitre-card:hover {
  box-shadow: 8px 8px 0 rgba(124,58,237,0.4), 0 16px 48px rgba(0,0,0,0.35);
}
:root[data-theme="dark"] .mitre-value {
  background: var(--gradient-text);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
:root[data-theme="dark"] .mitre-icon {
  color: #a78bfa;
}
:root[data-theme="dark"] .zk-card {
  background: var(--bg-card);
  border-color: rgba(124, 58, 237, 0.15);
  box-shadow: 6px 6px 0 rgba(124,58,237,0.3), 0 10px 40px rgba(0,0,0,0.25);
}
:root[data-theme="dark"] .zk-card:hover {
  box-shadow: 8px 8px 0 rgba(124,58,237,0.4), 0 16px 48px rgba(0,0,0,0.35);
}
:root[data-theme="dark"] .zk-card-title {
  color: var(--text-primary);
}
:root[data-theme="dark"] .zk-card-icon {
  color: #a78bfa;
  background: rgba(124, 58, 237, 0.1);
  border-color: rgba(124, 58, 237, 0.25);
}
:root[data-theme="dark"] .zk-feature-dot {
  background: linear-gradient(135deg, #a78bfa, var(--purple-deep));
}
:root[data-theme="dark"] .deploy-card {
  background: var(--bg-card);
  border-color: rgba(124, 58, 237, 0.15);
  box-shadow: 6px 6px 0 rgba(124,58,237,0.3), 0 10px 40px rgba(0,0,0,0.25);
}
:root[data-theme="dark"] .deploy-card:hover {
  box-shadow: 8px 8px 0 rgba(124,58,237,0.4), 0 16px 48px rgba(0,0,0,0.35);
}
:root[data-theme="dark"] .deploy-card-title {
  color: var(--text-primary);
}
:root[data-theme="dark"] .deploy-card-icon {
  color: #a78bfa;
  background: rgba(124, 58, 237, 0.1);
  border-color: rgba(124, 58, 237, 0.25);
}
:root[data-theme="dark"] .oss-card {
  background: var(--bg-card);
  border-color: rgba(124, 58, 237, 0.15);
  box-shadow: 6px 6px 0 rgba(124,58,237,0.3), 0 10px 40px rgba(0,0,0,0.25);
}
:root[data-theme="dark"] .oss-card:hover {
  box-shadow: 8px 8px 0 rgba(124,58,237,0.4), 0 16px 48px rgba(0,0,0,0.35);
}
:root[data-theme="dark"] .oss-tech-strip {
  border-top-color: rgba(124, 58, 237, 0.15);
}
:root[data-theme="dark"] .oss-tech-tag {
  background: rgba(6, 182, 212, 0.1);
  border-color: rgba(6, 182, 212, 0.3);
  color: #67e8f9;
}
:root[data-theme="dark"] .oss-extend-label {
  color: #67e8f9;
  background: rgba(6, 182, 212, 0.1);
  border-color: rgba(6, 182, 212, 0.3);
}
:root[data-theme="dark"] .oss-extend-bullet {
  background: #a78bfa;
}
:root[data-theme="dark"] .perf-card {
  background: var(--bg-card);
  border-color: rgba(124, 58, 237, 0.15);
  box-shadow: 6px 6px 0 rgba(124,58,237,0.3), 0 10px 40px rgba(0,0,0,0.25);
}
:root[data-theme="dark"] .perf-card:hover {
  box-shadow: 8px 8px 0 rgba(124,58,237,0.4), 0 16px 48px rgba(0,0,0,0.35);
}
:root[data-theme="dark"] .perf-value {
  background: var(--gradient-text);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
:root[data-theme="dark"] .perf-icon {
  color: #a78bfa;
  background: rgba(124, 58, 237, 0.1);
  border-color: rgba(124, 58, 237, 0.25);
}
:root[data-theme="dark"] .arch-card-list li::before {
  background: #a78bfa;
}
:root[data-theme="dark"] .decor-orb {
  opacity: 0.1;
}
:root[data-theme="dark"] .decor-line {
  background: linear-gradient(180deg, transparent, rgba(124, 58, 237, 0.12), transparent);
  opacity: 0.6;
}

/* ======== RESPONSIVE ======== */
@media (max-width: 1000px) {
  .arch-grid { grid-template-columns: 1fr; gap: 20px; }
  .mitre-grid { grid-template-columns: repeat(2, 1fr); }
  .deploy-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 750px) {
  .tech-hero { padding: 90px 20px 60px; min-height: 40vh; }
  .tech-title { font-size: 2.2em; }
  .tech-subtitle { font-size: 0.95em; }
  .tech-section { padding: 56px 16px; }
  .section-heading { font-size: 1.7em; margin-bottom: 36px; }
  .flow-timeline { padding-left: 48px; }
  .flow-timeline::before { left: 14px; }
  .flow-step-number { left: -48px; width: 32px; height: 32px; font-size: 11px; }
  .flow-step-connector { left: -33px; }
  .flow-step-card { padding: 22px 20px; }
  .mitre-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
  .mitre-value { font-size: 2.8em; }
  .zk-card { flex-direction: column; gap: 20px; padding: 32px 24px; }
  .deploy-grid { grid-template-columns: 1fr; }
  .deploy-card { padding: 28px 22px; }
  .oss-card { padding: 32px 24px; }
  .oss-extend-grid { grid-template-columns: 1fr; }
  .perf-grid { grid-template-columns: 1fr; gap: 20px; }
  .perf-value { font-size: 3em; }
}
</style>
